/**
 * Firestore へ書く直前に undefined を落とす。
 *
 * Firestore は undefined を値として受け付けず、
 * 「Unsupported field value: undefined」で書き込みごと失敗する。
 * この失敗は画面上「会計エラー」としか出ないため、
 * どの項目が原因なのか運用側からは分からない。
 *
 * このプロジェクトでは同じ原因で3回止まった:
 *   - Cloud Functions の出退勤（lateMinutes: undefined）
 *   - デモ投入スクリプト（employmentInfo.hourlyWage: undefined）
 *   - 会計の手当（staffId / note: undefined）
 * 保存の直前で必ず通す。
 */

/** undefined のキーを取り除いた複製を返す。ネストと配列も辿る。 */
export function stripUndefined<T>(value: T): T {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value
      .map((v) => stripUndefined(v))
      .filter((v) => v !== undefined) as unknown as T;
  }
  // Date / Timestamp / FieldValue など、独自クラスはそのまま通す
  if (typeof value === 'object') {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) return value;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      out[k] = stripUndefined(v);
    }
    return out as T;
  }
  return value;
}

/** undefined を含むキーの一覧。原因を画面に出したいときに使う。 */
export function findUndefinedPaths(value: unknown, prefix = ''): string[] {
  if (value === undefined) return [prefix || '(root)'];
  if (value === null || typeof value !== 'object') return [];
  if (Array.isArray(value)) {
    return value.flatMap((v, i) => findUndefinedPaths(v, `${prefix}[${i}]`));
  }
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return [];
  return Object.entries(value as Record<string, unknown>).flatMap(([k, v]) =>
    findUndefinedPaths(v, prefix ? `${prefix}.${k}` : k)
  );
}
